import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react'
import StaigeModal from '../styles/Modal'
import mobiscroll from '@mobiscroll/react'
import { setPlutoHeater, getPlutoHeaterStatus, PlutoHeaterStatus } from '../../api/api-pluto'

import './HeatControll.scss'

type HeatControllProps = {
  cameraId: string
  reachableCams: string[]
}

export interface HeatControllRef {
  handleOpenHeatControllModal: () => void
}

const MAX_DURATION = 300
const POLL_INTERVAL_MS = 30000

const HeatControll = forwardRef<HeatControllRef, HeatControllProps>((props, ref) => {
  const [durationSeconds, setDurationSeconds] = useState<number>(MAX_DURATION)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [heaterStatuses, setHeaterStatuses] = useState<PlutoHeaterStatus[]>([])
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0)

  const refModal = useRef<any>(null)

  const fetchStatuses = async () => {
    if (props.reachableCams.length === 0) return
    setIsLoadingStatus(true)
    try {
      const results = await Promise.all(
        props.reachableCams.map((hostname) => getPlutoHeaterStatus({ cameraId: props.cameraId, hostname }))
      )
      setHeaterStatuses(results)
    } catch (error) {
      console.error('Fehler beim Laden des Heater-Status:', error)
    } finally {
      setIsLoadingStatus(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const interval = setInterval(fetchStatuses, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isOpen, props.reachableCams, props.cameraId])

  useEffect(() => {
    if (remainingSeconds === 0 && heaterStatuses.some((s) => s.heaterEnabled)) {
      fetchStatuses()
    }
  }, [remainingSeconds])

  useEffect(() => {
    const activeStatus = heaterStatuses.find((s) => s.heaterEnabled)
    if (!activeStatus) {
      setRemainingSeconds(0)
      return
    }
    const endTime = activeStatus.manualControlStartTime + activeStatus.manualControlDuration
    const calc = () => Math.max(0, endTime - Math.floor(Date.now() / 1000))
    setRemainingSeconds(calc())
    const interval = setInterval(() => setRemainingSeconds(calc()), 1000)
    return () => clearInterval(interval)
  }, [heaterStatuses])

  useImperativeHandle(ref, () => ({
    handleOpenHeatControllModal: async () => {
      setDurationSeconds(MAX_DURATION)
      setHeaterStatuses([])
      setIsOpen(true)
      if (refModal.current) {
        refModal.current.handleOpenModal()
      }
      await fetchStatuses()
    }
  }))

  const handleClose = () => {
    setIsOpen(false)
    if (refModal.current) {
      refModal.current.handleCloseModal()
    }
  }

  const handleConfirm = async () => {
    const inactiveCams = heaterStatuses.filter((s) => !s.heaterEnabled).map((s) => s.hostname)

    if (inactiveCams.length === 0) return

    setIsSaving(true)
    try {
      await Promise.all(
        inactiveCams.map((hostname) =>
          setPlutoHeater({ cameraId: props.cameraId, hostname, durationSeconds })
        )
      )
      mobiscroll.toast({ message: 'Heizung erfolgreich aktiviert', color: 'success' })
      await fetchStatuses()
      handleClose()
    } catch (error) {
      console.error('Fehler beim Aktivieren des Heaters:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return '0 min'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return s === 0 ? `${m} min` : `${m}:${String(s).padStart(2, '0')} min`
  }

  const allActive = heaterStatuses.length > 0 && heaterStatuses.every((s) => s.heaterEnabled)
  const someActive = heaterStatuses.some((s) => s.heaterEnabled)
  const maxDuration =
    heaterStatuses.length > 0 ? Math.max(...heaterStatuses.map((s) => s.manualControlDuration)) : 0

  const isConfirmDisabled = isSaving || isLoadingStatus || props.reachableCams.length === 0 || allActive

  return (
    <StaigeModal
      ref={refModal}
      title='Heater Control'
      confirmText={isSaving ? 'Wird aktiviert...' : allActive ? 'Bereits aktiv' : 'Heater aktivieren'}
      onConfirm={handleConfirm}
      disabledConfirm={isConfirmDisabled}
    >
      <div className='heat-control'>
        {/* Status */}
        {isLoadingStatus && heaterStatuses.length === 0 ? (
          <div className='heat-status-loading'>Status wird geladen...</div>
        ) : heaterStatuses.length > 0 ? (
          <div className={`heat-status-item ${someActive ? 'active' : 'inactive'}`}>
            <span className='heat-status-dot' />
            <span className='heat-status-label'>Heizung</span>
            <span className='heat-status-duration'>
              {someActive ? formatDuration(maxDuration) : 'Inaktiv'}
            </span>
          </div>
        ) : null}

        {/* Countdown – wenn aktiv */}
        {allActive && <div className='heat-countdown'>{formatCountdown(remainingSeconds)}</div>}

        {/* Slider – nur wenn Status geladen und nicht alle aktiv */}
        {heaterStatuses.length > 0 && !allActive && (
          <>
            <div className='heat-duration-value'>{formatDuration(durationSeconds)}</div>
            <div className='heat-slider-wrapper'>
              <input
                type='range'
                min={0}
                max={MAX_DURATION}
                step={30}
                value={durationSeconds}
                onChange={(e) => setDurationSeconds(Number.parseInt(e.target.value, 10))}
                className='heat-slider'
                disabled={isSaving}
              />
              <div className='heat-slider-labels'>
                <span>0 min</span>
                <span>5 min</span>
              </div>
            </div>
            <div className='heat-cam-info'>
              {props.reachableCams.length === 0
                ? 'Kamera aktuell nicht erreichbar'
                : 'Heizung wird für alle Linsen aktiviert'}
            </div>
          </>
        )}
      </div>
    </StaigeModal>
  )
})

HeatControll.displayName = 'HeatControll'

export default HeatControll
